import { useEffect, useState } from "react";
import { Loader2, RefreshCw, UserCheck, Users2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  adminListWaitlist,
  adminAdmitAllWaitlist,
  adminAdmitWaitlistUser,
} from "@/lib/afritask";
import { formatApiError } from "@/lib/errors";

const PAGE_SIZE = 50;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminWaitlist() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminListWaitlist({ page, size: PAGE_SIZE, search: search || undefined });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load waitlist"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const runSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    load();
  };

  const admitAll = async () => {
    if (confirmText !== "ADMIT_ALL") {
      toast.error("Type ADMIT_ALL to confirm");
      return;
    }
    setBusy(true);
    try {
      const r = await adminAdmitAllWaitlist();
      toast.success(`Admitted ${r.admitted_count} user${r.admitted_count === 1 ? "" : "s"}`);
      setConfirmOpen(false);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Admit-all failed"));
    } finally {
      setBusy(false);
    }
  };

  const admitOne = async (userId) => {
    setRowBusy((s) => ({ ...s, [userId]: true }));
    try {
      const r = await adminAdmitWaitlistUser(userId);
      if (r.admitted) {
        toast.success("Admitted");
      } else {
        toast.info("User is no longer waitlisted");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Admit failed"));
    } finally {
      setRowBusy((s) => ({ ...s, [userId]: false }));
    }
  };

  const totalWl = data?.total_waitlisted ?? 0;
  const goal = data?.launch_goal ?? 100;
  const pct = Math.round(Math.min(1, data?.progress_pct ?? 0) * 100);
  const users = data?.users ?? [];
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="admin-waitlist-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Waitlist</h1>
          <p className="text-sm text-neutral-500">
            Manage the launch queue. Admission is independent of the waitlist_mode flag.
          </p>
        </div>
      </div>

      {/* Stat pills */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500">Waitlisted</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-[#B45309]">{totalWl}</p>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500">Goal</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{goal}</p>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500">Progress</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{pct}%</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={totalWl === 0}
          className="bg-[#B45309] text-white hover:bg-[#92400E]"
          data-testid="admin-waitlist-admit-all-btn"
        >
          <UserCheck className="mr-2 h-4 w-4" />
          Admit all
        </Button>
        <Button
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="admin-waitlist-refresh-btn"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        <form onSubmit={runSearch} className="ml-auto flex items-center gap-2">
          <Input
            placeholder="Search by name or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-56"
            data-testid="admin-waitlist-search-input"
          />
          <Button type="submit" variant="secondary">Search</Button>
        </form>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Position</th>
              <th className="px-4 py-2 text-left font-medium">Name</th>
              <th className="px-4 py-2 text-left font-medium">Phone</th>
              <th className="px-4 py-2 text-left font-medium">Waitlisted since</th>
              <th className="px-4 py-2 text-left font-medium">Referrals</th>
              <th className="px-4 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-4 py-6 text-center text-neutral-500" colSpan={6}>
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td className="px-4 py-8 text-center text-neutral-500" colSpan={6} data-testid="admin-waitlist-empty">
                  <div className="mx-auto flex max-w-md flex-col items-center gap-2">
                    <Users2 className="h-5 w-5" />
                    <p>No one is waitlisted right now.</p>
                    <p className="text-xs">
                      Flip <span className="font-medium">Waitlist mode</span> ON in Settings to start collecting signups.
                    </p>
                  </div>
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-neutral-100">
                <td className="px-4 py-2 tabular-nums text-neutral-500">#{u.position}</td>
                <td className="px-4 py-2 font-medium">{u.display_name || u.username}</td>
                <td className="px-4 py-2 tabular-nums text-neutral-600">{u.phone || "—"}</td>
                <td className="px-4 py-2 text-neutral-600">{fmtDate(u.waitlisted_at)}</td>
                <td className="px-4 py-2 tabular-nums">
                  {u.referral_count_waitlisted > 0 ? (
                    <span className="rounded-full bg-[#FDF3E4] px-2 py-0.5 text-xs font-medium text-[#B45309]">
                      {u.referral_count_waitlisted}
                    </span>
                  ) : (
                    <span className="text-neutral-400">0</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => admitOne(u.user_id)}
                    disabled={!!rowBusy[u.user_id]}
                    data-testid={`admin-waitlist-admit-${u.user_id}`}
                  >
                    Admit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Prev
          </Button>
          <span className="text-neutral-500">Page {page} of {pages}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
          >
            Next
          </Button>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={(v) => { setConfirmOpen(v); if (!v) setConfirmText(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Admit all waitlisted users?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-600">
            This will admit <span className="font-semibold">{totalWl}</span> user{totalWl === 1 ? "" : "s"}.
            They&apos;ll immediately get dashboard access. This cannot be undone.
          </p>
          <p className="text-sm text-neutral-600">
            Type <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono">ADMIT_ALL</span> to confirm.
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="ADMIT_ALL"
            data-testid="admin-waitlist-admit-all-confirm-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              className="bg-[#B45309] text-white hover:bg-[#92400E]"
              onClick={admitAll}
              disabled={busy || confirmText !== "ADMIT_ALL"}
              data-testid="admin-waitlist-admit-all-confirm-btn"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Admit all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
